// Breadcrumb component voor het tonen van het huidige pad
import { Fragment } from "react"
import { useEffect, useState } from "react"
import { format } from "date-fns"
import EnkPageContainer from "../components/page-container"
import { classNames } from "../utils/styling-utils"
import { TEnkIWPDocument, EnkIWPServerDocuments, TEnkIWPDocumentLocation } from "../api/api-iwp-server"
import EnkContentLoadingAndError from "../components/error-box"
import { LOCATION_SEPARATOR } from "../components/common/breadcrum"
import EnkBreadcrumb from "../components/common/breadcrum"
import { useAuth } from "../auth/use-auth"
import { FolderIcon } from "@heroicons/react/24/outline"

function formatFileSize(size: number) {
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
  return `${(size / (1024 * 1024)).toFixed(1)} MB`
}

function formatDate(xmlDate: string): string {
  try {
    const date = new Date(xmlDate)
    return format(date, "dd-MM-yy HH:mm:ss")
  } catch (error) {
    console.error("Failed to format date:", error)
    return xmlDate // Fallback to original value if formatting fails
  }
}

function locationRoot(location: string): string {
  if (!location) return ""
  const parts = location.split(LOCATION_SEPARATOR)
  return parts[0]
}

function locationPath(location: string): string {
  if (!location) return ""
  const parts = location.split(LOCATION_SEPARATOR)
  return parts.slice(1).join(LOCATION_SEPARATOR)
}

function EnkSubFolderItem({ name, onClick }: { name: string; onClick: () => void }) {
  return (
    <tr key="go-up">
      <td className="border-b border-gray-200 whitespace-nowrap py-5 pl-4 pr-3 text-sm font-medium text-gray-900 cursor-pointer" onClick={onClick}>
        <span className="flex items-center">
          <FolderIcon className="mr-2 h-5 w-5 text-gray-400" />
          <span>{name}</span>
        </span>
      </td>
      <td className="border-b border-gray-200 whitespace-nowrap px-3 py-4 text-sm text-gray-500"></td>
      <td className="border-b border-gray-200 hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-right lg:table-cell"></td>
      <td className="border-b border-gray-200 hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 lg:table-cell"></td>
      <td className="border-b border-gray-200 relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-8 lg:pr-8"></td>
    </tr>
  )
}

export default function EnkDocuments() {
  const [documents, setDocuments] = useState<TEnkIWPDocument[]>([])
  const [loadingLocations, setLoadingLocations] = useState<boolean>(true)
  const [loadingDocuments, setLoadingDocuments] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [locations, setLocations] = useState<TEnkIWPDocumentLocation[]>([])
  const [currentLocation, setCurrentLocation] = useState<TEnkIWPDocumentLocation | null>(null)
  const { getCurrentGroupId } = useAuth()

  useEffect(() => {
    async function fetchLocations() {
      try {
        const currentgrp = getCurrentGroupId()
        const fetchedLocations = await EnkIWPServerDocuments.getLocations(currentgrp)
        setLocations(fetchedLocations)
        if (fetchedLocations.length > 0) {
          setCurrentLocation(fetchedLocations[0] || "")
        }
      } catch (error) {
        console.error("Failed to fetch locations:", error)
        setError("Fout bij het laden van locaties")
      }
      setLoadingLocations(false)
    }

    void fetchLocations()
  }, [getCurrentGroupId])

  useEffect(() => {
    async function fetchDocuments() {
      if (!currentLocation) {
        setLoadingDocuments(false)
        return
      }
      try {
        setLoadingDocuments(true)
        setError(null) // Reset error state before fetching
        const fetchedDocuments = await EnkIWPServerDocuments.getDocuments(getCurrentGroupId(), currentLocation.location || "")
        setDocuments(fetchedDocuments)
      } catch (error) {
        console.error("Failed to fetch documents:", error)
        setError("Fout bij het laden van documenten")
      }
      setLoadingDocuments(false)
    }

    void fetchDocuments()
  }, [currentLocation, getCurrentGroupId])

  const handleFolderClick = (doc: TEnkIWPDocument) => {
    if (doc.isSubDir) {
      //setCurrentLocation(doc)
      //set currentLocation to currentlocation with an updated location -> location + | + doc.id
      const newLocation = `${currentLocation?.location}${LOCATION_SEPARATOR}${doc.id}`
      if (currentLocation) {
        setCurrentLocation({ ...currentLocation, location: newLocation })
      }
      console.log("Folder clicked:", doc.location, doc.id)
    }
  }

  const handleDownloadClick = async (doc: TEnkIWPDocument) => {
    if (!doc.location || !doc.id) {
      console.error("Invalid document location or ID")
      return
    }
    try {
      const stream = await EnkIWPServerDocuments.getDocument(getCurrentGroupId(), doc.location, doc.id)
      const streamUrl = stream.url
      const response = await fetch(streamUrl)
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = (doc.name || "document") + "." + (doc.docType || "txt")
      a.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error("Failed to download document:", error)
    }
  }

  // Handler voor terug naar bovenliggende map
  const handleGoUp = () => {
    if (!currentLocation || !currentLocation.location) return
    const loc = currentLocation.location
    const lastSep = loc.lastIndexOf(LOCATION_SEPARATOR)
    if (lastSep === -1) return
    const newLoc = loc.substring(0, lastSep)
    setCurrentLocation({ ...currentLocation, location: newLoc })
  }

  const handleBreadcrumbNavigate = (loc: string) => {
    let newLoc = locationRoot(currentLocation?.location || "")
    if (loc) newLoc += LOCATION_SEPARATOR + loc
    setCurrentLocation(currentLocation ? { ...currentLocation, location: newLoc } : null)
  }

  const inSubMap = !!currentLocation?.location && currentLocation.location.includes(LOCATION_SEPARATOR)

  return (
    <EnkPageContainer>
      <div className="flex">
        {/* Left Panel for Locations medium screens and larger*/}
        <div className="hidden md:flex w-1/4 border-r border-gray-300 p-4 pl-0">
          <ul className="mt-4 space-y-2">
            {locations.map((location) => (
              <li
                key={location.location}
                className={classNames(
                  location.location === locationRoot(currentLocation?.location || "")
                    ? "bg-gray-100 text-primary"
                    : "text-gray-700 hover:bg-gray-50 hover:text-primary",
                  "group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6"
                )}
                onClick={() => setCurrentLocation(location)}
              >
                {location.name}
              </li>
            ))}
          </ul>
        </div>

        {/* Main Content for Documents */}
        <div className="w-full md:w-3/4 ">
          {/* Location indicator for small screens */}
          <div className="md:hidden -mx-2 px-4 py-2 bg-gray-100 text-sm font-semibold text-gray-900 flex items-center">
            <label htmlFor="location-select" className="mr-2 text-sm text-gray-700">
              Locatie:
            </label>
            <select
              id="location-select"
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary text-sm"
              value={currentLocation?.name}
              onChange={(e) => {
                const location = e.target.value
                const newlocation = locations.find((loc) => loc.name === location)
                setCurrentLocation(newlocation || null)
              }}
            >
              {locations.map((location) => (
                <option key={location.location} value={location.name}>
                  {location.name}
                </option>
              ))}
            </select>
          </div>

          {/* Breadcrumb boven de documentenlijst */}
          <div className="mt-4 md:mt-8 md:ml-8 flow-root">
            <div className="-mx-1">
              <EnkBreadcrumb
                location={locationPath(currentLocation?.location || "") || ""}
                onNavigate={handleBreadcrumbNavigate}
                rootName={currentLocation?.name || "Root"}
              />
            </div>
            <div className="-mx-4 -my-2">
              <EnkContentLoadingAndError error={error} loading={loadingDocuments || loadingLocations}>
                <div className="inline-block min-w-full py-2 align-middle">
                  <table className="min-w-full border-separate border-spacing-0">
                    <thead>
                      <tr className="hover:bg-gray-50">
                        <th
                          scope="col"
                          className="sticky top-0 z-10 border-b border-gray-300 bg-white/75 py-3.5 px-3 text-left text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter"
                        >
                          Naam
                        </th>
                        <th
                          scope="col"
                          className="sticky top-0 z-10 border-b border-gray-300 bg-white/75 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter"
                        >
                          Type
                        </th>
                        <th
                          scope="col"
                          className="sticky top-0 z-10 hidden border-b border-gray-300 bg-white/75 px-3 py-3.5 text-right text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter lg:table-cell"
                        >
                          Grootte
                        </th>
                        <th
                          scope="col"
                          className="sticky top-0 z-10 hidden border-b border-gray-300 bg-white/75 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter lg:table-cell"
                        >
                          Laatst gewijzigd
                        </th>
                        <th
                          scope="col"
                          className="sticky top-0 z-10 border-b border-gray-300 bg-white/75 py-3.5 pl-3 pr-4 backdrop-blur backdrop-filter sm:pr-6 lg:pr-8"
                        >
                          <span className="sr-only">Acties</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {documents.length === 0 && (
                        <tr>
                          <td colSpan={5} className="text-center py-4 text-sm text-gray-500">
                            Geen documenten gevonden.
                          </td>
                        </tr>
                      )}

                      {/* Extra item voor terug als je in een submap zit */}
                      {inSubMap && <EnkSubFolderItem name=".." onClick={handleGoUp} />}

                      {documents.map((document, docIdx) => (
                        <Fragment key={docIdx}>
                          {document.isSubDir ? (
                            <EnkSubFolderItem name={document.name || "<naamloos>"} onClick={() => handleFolderClick(document)} />
                          ) : (
                            <tr key={docIdx}>
                              <td
                                className={classNames(
                                  docIdx !== documents.length - 1 ? "border-b border-gray-200" : "",
                                  "whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900"
                                )}
                              >
                                <span className="flex items-center cursor-default" onClick={() => handleFolderClick(document)}>
                                  {document.isSubDir && <FolderIcon className="mr-2 h-5 w-5 text-gray-400 pointer-events-none" />}
                                  <span>{document.name}</span>
                                </span>
                              </td>
                              <td
                                className={classNames(
                                  docIdx !== documents.length - 1 ? "border-b border-gray-200" : "",
                                  "whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                                )}
                              >
                                {document.docType}
                              </td>
                              <td
                                className={classNames(
                                  docIdx !== documents.length - 1 ? "border-b border-gray-200" : "",
                                  "hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-right lg:table-cell"
                                )}
                              >
                                {!document.isSubDir && <>{formatFileSize(document.size)}</>}
                              </td>
                              <td
                                className={classNames(
                                  docIdx !== documents.length - 1 ? "border-b border-gray-200" : "",
                                  "hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 lg:table-cell"
                                )}
                              >
                                {!document.isSubDir && <>{formatDate(document.lastChanged)}</>}
                              </td>
                              <td
                                className={classNames(
                                  docIdx !== documents.length - 1 ? "border-b border-gray-200" : "",
                                  "relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-8 lg:pr-8"
                                )}
                              >
                                <div className="flex flex-none items-center gap-x-4">
                                  {!document.isSubDir && (
                                    <a
                                      onClick={() => void handleDownloadClick(document)}
                                      className="rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:block"
                                    >
                                      Download<span className="sr-only">, download {document.name}</span>
                                    </a>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </EnkContentLoadingAndError>
            </div>
          </div>
        </div>
      </div>
    </EnkPageContainer>
  )
}
